import { api } from './client';

// Types for game configuration DTOs

export interface UnitConfig {
  name: string;
  offense: number;
  defense: number;
  baseCost: number;
}

export interface RaceConfig {
  id: string;
  name: string;
  units: {
    offenseSpecialist: UnitConfig;
    defenseSpecialist: UnitConfig;
    elite: UnitConfig;
  };
  modifiers: Record<string, number>;
  description: string;
}

export interface PersonalityConfig {
  id: string;
  name: string;
  modifiers: Record<string, number>;
  description: string;
}

export interface DraftRateConfig {
  id: string;
  label: string;
  speedPercent: number;
  costPerSoldier: number;
}

export interface BuildingConfig {
  id: string;
  name: string;
  description: string;
  capacity: number;
  pairedWith: string | null;
  effects: Record<string, number>;
}

export interface RacesResponse {
  races: RaceConfig[];
  version: string;
}

export interface PersonalitiesResponse {
  personalities: PersonalityConfig[];
  version: string;
}

export interface DraftRatesResponse {
  rates: DraftRateConfig[];
  maxDraftTarget: number;
  version: string;
}

export interface BuildingsResponse {
  buildings: BuildingConfig[];
  version: string;
}

// Module-level cache for static game configuration
let racesCache: RacesResponse | null = null;
let personalitiesCache: PersonalitiesResponse | null = null;
let draftRatesCache: DraftRatesResponse | null = null;
let buildingsCache: BuildingsResponse | null = null;

/**
 * Fetches race configurations from the API.
 * Caches results since this data is static.
 */
export async function getRaces(): Promise<{
  data: RacesResponse | null;
  error: string | null;
}> {
  if (racesCache) {
    return { data: racesCache, error: null };
  }

  const response = await api.get<RacesResponse>('/api/game/races');

  if (response.error) {
    return { data: null, error: response.error };
  }

  racesCache = response.data;
  return { data: response.data, error: null };
}

/**
 * Fetches personality configurations from the API.
 * Caches results since this data is static.
 */
export async function getPersonalities(): Promise<{
  data: PersonalitiesResponse | null;
  error: string | null;
}> {
  if (personalitiesCache) {
    return { data: personalitiesCache, error: null };
  }

  const response = await api.get<PersonalitiesResponse>('/api/game/personalities');

  if (response.error) {
    return { data: null, error: response.error };
  }

  personalitiesCache = response.data;
  return { data: response.data, error: null };
}

/**
 * Fetches draft rate configurations from the API.
 * Caches results since this data is static.
 */
export async function getDraftRates(): Promise<{
  data: DraftRatesResponse | null;
  error: string | null;
}> {
  if (draftRatesCache) {
    return { data: draftRatesCache, error: null };
  }

  const response = await api.get<DraftRatesResponse>('/api/game/draft-rates');

  if (response.error) {
    return { data: null, error: response.error };
  }

  draftRatesCache = response.data;
  return { data: response.data, error: null };
}

/**
 * Fetches building configurations from the API.
 * Caches results since this data is static.
 */
export async function getBuildings(): Promise<{
  data: BuildingsResponse | null;
  error: string | null;
}> {
  if (buildingsCache) {
    return { data: buildingsCache, error: null };
  }

  const response = await api.get<BuildingsResponse>('/api/game/buildings');

  if (response.error) {
    return { data: null, error: response.error };
  }

  buildingsCache = response.data;
  return { data: response.data, error: null };
}

/**
 * Gets race configuration by ID.
 * Returns undefined if race not found or not yet loaded.
 */
export function getRaceById(raceId: string): RaceConfig | undefined {
  return racesCache?.races.find((r) => r.id === raceId.toUpperCase());
}

/**
 * Gets unit stats for a specific race and unit type.
 * Falls back to generic values if race not loaded.
 */
export function getUnitStats(
  raceId: string,
  unitType: 'offenseSpecialist' | 'defenseSpecialist' | 'elite'
): UnitConfig | undefined {
  const race = getRaceById(raceId);
  return race?.units[unitType];
}

/**
 * Clears all cached game configuration data.
 * Call this if configuration version changes.
 */
export function clearGameConfigCache(): void {
  racesCache = null;
  personalitiesCache = null;
  draftRatesCache = null;
  buildingsCache = null;
}

/**
 * Resets all game config caches. Used in tests for isolation.
 */
export function _resetGameConfigCache(): void {
  racesCache = null;
  personalitiesCache = null;
  draftRatesCache = null;
  buildingsCache = null;
}
